import React from 'react';
import { Sparkle, UsersThree } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import BrandLogo from '../ui/BrandLogo';

interface AuthShellProps {
  eyebrow: string;
  title: string;
  description: string;
  children: React.ReactNode;
}

const AuthShell: React.FC<AuthShellProps> = ({ eyebrow, title, description, children }) => (
  <main className="relative min-h-screen-safe overflow-hidden bg-slate-50 px-4 py-8 text-slate-900 dark:bg-slate-950 dark:text-white sm:px-6">
    <div aria-hidden="true" className="absolute -left-28 -top-28 h-80 w-80 rounded-full bg-cyan-400/20 blur-3xl dark:bg-cyan-400/15" />
    <div aria-hidden="true" className="absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-violet-500/20 blur-3xl dark:bg-violet-500/15" />
    <div className="relative mx-auto flex min-h-screen-safe-minus-4 w-full max-w-5xl items-center justify-center">
      <div className="grid w-full items-center gap-10 lg:grid-cols-[1fr_440px]">
        <section className="hidden lg:block">
          <div className="mb-8">
            <BrandLogo className="h-auto w-44" />
            <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">People make the map</div>
          </div>
          <h1 className="max-w-xl text-5xl font-semibold leading-[1.08] tracking-[-0.04em]">Find the bright moments happening around you.</h1>
          <p className="mt-5 max-w-lg text-lg leading-8 text-slate-600 dark:text-slate-300">Share a location, leave a status, and discover the people and energy nearby.</p>
          <p className="mt-2 max-w-lg text-xs leading-5 text-slate-500 dark:text-slate-400">Map content is not verified. Treat every status, identity, and location as fictional game content—not fact.</p>
          <div className="mt-8 flex gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-2 text-sm shadow-sm ring-1 ring-slate-200 dark:bg-white/[0.06] dark:ring-white/10"><UsersThree size={18} className="text-cyan-500" /> Community first</span>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-2 text-sm shadow-sm ring-1 ring-slate-200 dark:bg-white/[0.06] dark:ring-white/10"><Sparkle size={18} className="text-violet-500" /> Live map stories</span>
          </div>
        </section>

        <section className="w-full">
          <div className="mb-7 text-center lg:text-left">
            <BrandLogo iconOnly className="mx-auto mb-5 h-14 w-14 drop-shadow-[0_10px_18px_rgba(7,82,213,0.2)] lg:hidden" />
            <div className="mb-1 flex items-center justify-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-300 lg:justify-start"><Sparkle size={13} weight="fill" /> {eyebrow}</div>
            <h2 className="text-3xl font-semibold tracking-[-0.03em]">{title}</h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{description}</p>
          </div>
          {children}
          <div className="mt-6 text-center text-xs text-slate-500 lg:text-left dark:text-slate-400">
            mapbl is a fantasy experience for fun.{' '}
            <Link className="font-medium text-cyan-700 underline-offset-2 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 dark:text-cyan-300" to="/privacy">
              Privacy Policy
            </Link>
          </div>
        </section>
      </div>
    </div>
  </main>
);

export default AuthShell;
